<template>
  <div class="order-card" @click="$emit('card-click', order.id)">
    <div class="order-card-header">
      <OrderLabel :order="order" :orderService="orderService" />
      <WatchLaterButton
        id="tooltippopup-to-watch-later-button"
        v-if="isWatchLaterButtonVisible"
        class="ml-auto"
        isShowButtonText
        :isRegisteredWatchLater="isRegisteredWatchLater"
        @click="$emit('change-watch-later', $event)"
      />
    </div>
    <div
      v-if="willBeOutbreakingInfection"
      class="d-flex align-items-center caution-box"
      data-testid="infection-information-banner"
    >
      <FeatherIcon icon="alert-triangle" size="20" />
      <div class="ml-2">
        <p class="caution-text text-size-18">
          {{ infectionName }}が発生しています
        </p>
        <p class="caution-text">感染者への直接介助を伴う可能性があります</p>
      </div>
    </div>
    <BeginnerOrderLabel :order="order" :orderService="orderService" />
    <div class="d-flex align-items-center justify-content-between mt-1">
      <span
        class="order-card-title--break"
        :class="{ 'order-card-title--ellipsis': isTitleEllipsis }"
        >{{ orderTitle }}</span
      >
    </div>
    <div v-if="isAbTest2807TestGroup" class="d-flex justify-content-end mt-1">
      <Tag
        :label="applicantsCountLabel"
        :class="abTest2807Class"
        class="applicants-count-tag"
      />
    </div>

    <!-- 吹き出し -->
    <TooltipPopup
      position="bottom"
      targetSelector="#tooltippopup-to-watch-later-button"
    >
      <div>
        あとで見返したいお仕事は<TooltipHighlightText>あとで見る</TooltipHighlightText>！
      </div>
    </TooltipPopup>

    <!-- カルーセル -->
    <div v-if="isCarousel" class="mt-2 mx-n3">
      <BCarousel
        :id="'carousel' + order.id"
        indicators
        controls
        class="order-card-carousel"
      >
        <BCarouselSlide v-if="getOptimizedOrderImage(order.image1)">
          <template #img>
            <img
              :src="getOptimizedOrderImage(order.image1)"
              class="carousel-image"
            />
          </template>
        </BCarouselSlide>
        <BCarouselSlide v-if="getOptimizedOrderImage(order.image2)">
          <template #img>
            <img
              :src="getOptimizedOrderImage(order.image2)"
              class="carousel-image"
            />
          </template>
        </BCarouselSlide>
        <BCarouselSlide v-if="getOptimizedOrderImage(order.image3)">
          <template #img>
            <img
              :src="getOptimizedOrderImage(order.image3)"
              class="carousel-image"
            />
          </template>
        </BCarouselSlide>
      </BCarousel>
    </div>

    <!-- 横並び画像 -->
    <div v-else class="order-card-images mt-2">
      <div
        v-if="getOptimizedOrderImageSmall(order.image1)"
        class="order-card-images__item"
      >
        <BImgLazy
          :src="getOptimizedOrderImageSmall(order.image1)"
          alt=""
          class="order-card-images__item-img"
        />
      </div>
      <div
        v-if="getOptimizedOrderImageSmall(order.image2)"
        class="order-card-images__item"
      >
        <BImgLazy
          :src="getOptimizedOrderImage(order.image2)"
          alt=""
          class="order-card-images__item-img"
        />
      </div>
      <div
        v-if="getOptimizedOrderImage(order.image3)"
        class="order-card-images__item"
      >
        <BImgLazy
          :src="getOptimizedOrderImage(order.image3)"
          alt=""
          class="order-card-images__item-img"
        />
      </div>
    </div>

    <div class="office-info">
      <div>
        <div class="office-name-content">
          <span class="office-name mr-2">{{ officeName }}</span>
          <EvaluationWithCount
            v-if="evaluationTotalAverage"
            :value="evaluationTotalAverage"
            :count="evaluationCount"
            text-size="12"
            size="12"
          />
        </div>
        <div class="d-flex align-items-center">
          <FeatherIcon icon="map-pin" size="14" class="mr-1 text-mild" />
          <span class="office-address">{{ officeAddress }}</span>
        </div>
      </div>
      <div class="office-actions">
        <FavoriteButton
          id="tooltippopup-to-favorite-button"
          v-if="showOfficeFavoriteButton"
          :isFavorite="isFavorite"
          @click="$emit('favorite-change', $event)"
        />
        <OfficeMuteButton
          :isMuted="isMutedOffice"
          @click="$emit('change-muted-office', $event)"
        />
      </div>
    </div>

    <!-- 吹き出し -->
    <TooltipPopup
      position="top"
      targetSelector="#tooltippopup-to-favorite-button"
    >
      <div>
        気に入った事業所は<TooltipHighlightText>お気に入り</TooltipHighlightText>に！
      </div>
    </TooltipPopup>

    <div class="work-time mt-2" ref="workTime" data-testid="work-time">
      <div v-if="application" class="application">
        <div class="pl-4 work-time-detail">
          <RemainingDateTime :isApplied="true" :isHiring="order.isHiring" />
          <div>
            <span class="work-time">{{ appliedWorkTimeLabel }}</span>
            <span class="rest-time">{{ appliedRestTimeLabel }}</span>
          </div>
          <div class="salary-per-day-wrapper">
            <span class="salary-per-day-with-various-allowances"
              >{{ appliedSalaryPerDayWithVariousAllowances }}円</span
            >
            <Tag
              v-if="hasAppliedNightWorkSalary(application)"
              label="深夜手当"
              class="extra-wage-tag"
            />
            <Tag
              v-if="hasAppliedLegalOvertimeSalary(application)"
              label="残業手当"
              class="extra-wage-tag"
            />
          </div>
        </div>
      </div>
      <div
        v-else
        class="d-flex flex-column align-items-start justify-content-between work-time-options"
      >
        <div
          v-for="option in order.workTimeOptions"
          :key="option.id"
          class="d-flex work-time-detail"
          :class="{ expired: !isWorkTimeOptionAvailable(option) }"
          role="button"
          @click="onChangeSelectedWorkTimeOptionId(option)"
        >
          <div class="d-flex align-items-center">
            <AppRadio
              :id="`work-time-option-${option.id}`"
              name="workTimeOption"
              class="work-time-option-radio"
              :hasBorder="false"
              :value="option.id"
              :checked="option.id === innerSelectedWorkTimeOptionId"
              :class="{ invisible: !isWorkTimeOptionAvailable(option) }"
              text=""
            />
          </div>
          <div>
            <RemainingDateTime
              :expiredAt="getVisibleToDate(option)"
              :isHiring="order.isHiring"
            />
            <div>
              <span class="work-time">{{ getOrderWorkTimeLabel(option) }}</span>
              <span class="rest-time">{{ getOrderRestTimeLabel(option) }}</span>
            </div>
            <div>
              <div class="salary-per-day-wrapper">
                <span class="salary-per-day-with-various-allowances"
                  >{{ getSalaryPerDayWithVariousAllowances(option) }}円</span
                >
                <Tag
                  v-if="hasNightWorkSalary(option)"
                  label="深夜手当"
                  class="extra-wage-tag"
                />
                <Tag
                  v-if="hasLegalOvertimeSalary(option)"
                  label="残業手当"
                  class="extra-wage-tag"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="reward-detail">
        <div class="ml-auto hourly-wage" data-testid="hourly-wage">
          {{ hourlyWageLabel }}
        </div>
        <div
          class="ml-auto various-allowances"
          data-testid="various-allowances"
        >
          {{ variousAllowancesLabel }}
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import EvaluationWithCount from "@/components/molecules/other/EvaluationWithCount.vue";
import OrderLabel from "@/components/molecules/orders/OrderLabel.vue";
import BeginnerOrderLabel from "@/components/molecules/orders/BeginnerOrderLabel.vue";
import FavoriteButton from "@/components/organisms/common/FavoriteButton.vue";
import RemainingDateTime from "@/components/organisms/orders/RemainingDateTime.vue";
import AppRadio from "@/components/atoms/input/AppRadio.vue";
import FeatherIcon from "@/components/atoms/other/FeatherIcon.vue";
import { getOptimizedImageUrl, isDataUri } from "@/utils/image";
import { orderService } from "@/app/services/OrderService";
import { Component, Prop, Vue } from "vue-facing-decorator";
import { OrderDetail } from "@/types/order/Order";
import {
  OrderDetailWorkTimeOption,
  OrderWorkTimeOption
} from "@/types/order/OrderWorkTimeOption";
import { formatNumber } from "@/utils/number";
import { formatFromISOString, now, parseISO } from "@/utils/date";
import { Application } from "@/types/order/Application";
import WatchLaterButton from "@/components/atoms/button/WatchLaterButton.vue";
import { authStore } from "@/store/auth";
import ExternalLink from "@/const/externalLink";
import { isBefore, parse } from "@/utils/date";
import OfficeMuteButton from "@/components/organisms/common/OfficeMuteButton.vue";
import Tag from "@/components/atoms/other/Tag.vue";
import TooltipPopup from "@/components/molecules/other/TooltipPopup.vue";
import TooltipHighlightText from "@/components/atoms/other/TooltipHighlightText.vue";
import { AbTest2807 } from "@/app/services/AbTestService";

@Component({
  components: {
    AppRadio,
    EvaluationWithCount,
    FavoriteButton,
    FeatherIcon,
    RemainingDateTime,
    OrderLabel,
    BeginnerOrderLabel,
    OfficeMuteButton,
    WatchLaterButton,
    Tag,
    TooltipPopup,
    TooltipHighlightText
  }
})
export default class OrderCard extends Vue {
  @Prop({ type: Boolean, default: false }) readonly isPreview!: boolean;
  @Prop({ type: Object, required: true }) readonly order!: OrderDetail;
  @Prop({ type: Object, default: null })
  readonly application!: Application | null;
  @Prop({ type: Boolean, default: false }) readonly isLogin!: boolean;
  @Prop({ type: Boolean, default: false }) readonly isCarousel!: boolean;
  @Prop({ type: Boolean, default: false }) readonly displayCarfare!: boolean;
  @Prop({ type: Boolean, default: false }) readonly isTitleEllipsis!: boolean;
  @Prop({ type: Boolean, default: false }) readonly isFavorite!: boolean;
  @Prop({ type: Boolean, default: false }) readonly isMutedOffice!: boolean;
  // TODO: 変数名がサービス層のorderServiceと被っているので修正する
  @Prop({ type: String, required: true }) readonly orderService!: string;
  @Prop({ type: Number, required: false }) readonly evaluationTotalAverage!:
    | number
    | null;
  @Prop({ type: Number, required: true }) readonly evaluationCount!: number;
  @Prop({ type: Number, required: false }) readonly selectedWorkTimeOptionId!:
    | number
    | null;
  @Prop({ type: Boolean, default: false })
  readonly isRegisteredWatchLater!: boolean;
  @Prop({ type: Boolean, default: true })
  readonly showOfficeFavoriteButton!: boolean;
  @Prop({ type: Boolean, default: true })
  readonly isShownWatchLaterButton!: boolean;

  orderDateListScrollLeft = 0;
  isOrderDateListScrollStart = true;
  isOrderDateListScrollEnd = false;

  get externalLink() {
    return ExternalLink;
  }

  get user() {
    return authStore.user;
  }

  get innerSelectedWorkTimeOptionId(): number | null {
    return this.selectedWorkTimeOptionId;
  }
  set innerSelectedWorkTimeOptionId(value: number | null) {
    this.$emit("update:selectedWorkTimeOptionId", value);
  }

  get orderDate() {
    if (this.isPreview && !this.order.date) {
      return "未設定";
    }
    return formatFromISOString(this.order.date, "MM/dd(iiiii)");
  }

  get orderTitle() {
    return this.order.title;
  }
  get officeName() {
    return this.order.office.name;
  }
  get officeAddress() {
    const office = this.order.office;
    if (this.isPreview && (!office || !office.address)) {
      return "未設定";
    }
    return office ? office.address : null;
  }
  get hourlyWageLabel() {
    const hourlyWage = this.application
      ? this.application.hourlyWage
      : this.order.hourlyWage;
    if (this.isPreview && isNaN(hourlyWage)) {
      return "時給 未設定";
    }
    return `時給${formatNumber(hourlyWage)}円`;
  }
  get variousAllowancesLabel(): string {
    const variousAllowances = this.application
      ? this.application.variousAllowances
      : this.order.variousAllowances;
    if (this.isPreview && isNaN(variousAllowances)) {
      return "交通費 未設定";
    }
    if (variousAllowances === 0) {
      return "";
    }
    return `交通費${formatNumber(variousAllowances)}円込`;
  }
  get appliedWorkTimeLabel(): string {
    if (!this.application) return "";
    return orderService.getFormattedWorkTimeRangeFromDate(
      this.application.appliedStartWorkAt,
      this.application.appliedFinishWorkAt
    );
  }
  get appliedRestTimeLabel(): string {
    if (!this.application || !this.application.appliedHasRest) return "";
    return (
      "休憩: " +
      orderService.getFormattedWorkTimeRangeFromDate(
        this.application.appliedStartRestAt,
        this.application.appliedFinishRestAt
      )
    );
  }
  get appliedSalaryPerDayWithVariousAllowances(): string {
    if (!this.application) return "";
    return (
      formatNumber(
        orderService.getAppliedSalaryPerDayWithVariousAllowances(
          this.application
        )
      ) || ""
    );
  }

  // 案件の募集日に感染症が発生しているかをチェック
  get willBeOutbreakingInfection() {
    if (!this.order.office.expectedConvergenceDate) {
      return false;
    }
    const expectedConvergenceDate = parse(
      this.order.office.expectedConvergenceDate,
      "yyyy-MM-dd"
    );
    const orderDate = parse(this.order.date, "yyyy-MM-dd");
    return !isBefore(expectedConvergenceDate, orderDate);
  }

  get infectionName() {
    return this.order.office.infectionName || "";
  }

  get isWatchLaterButtonVisible() {
    if (!this.isShownWatchLaterButton) {
      return false;
    }
    return orderService.isWatchLaterButtonVisible(this.order);
  }

  get isAbTest2807TestGroup(): boolean {
    return AbTest2807.getTestGroup() !== "A";
  }

  get abTest2807Class(): string | null {
    const testGroup = AbTest2807.getTestGroup();
    if (testGroup === "A") {
      return null;
    }
    return `ab-test-group-2807-${testGroup.toLowerCase()}`;
  }

  get applicantsCountLabel(): string {
    return `募集人数 ${this.order.applicantsCount}/${this.order.admittedCount}人`;
  }

  getOrderWorkTimeLabel(option: OrderWorkTimeOption): string {
    if (!option) return "";
    if (this.isPreview && (!option.startWorkAt || !option.finishWorkAt)) {
      return "未設定";
    }
    return orderService.getFormattedWorkTimeRangeFromDate(
      option.startWorkAt,
      option.finishWorkAt
    );
  }
  getOrderRestTimeLabel(option: OrderWorkTimeOption): string {
    if (!option || !option.hasRest) {
      return "";
    }
    return (
      "休憩: " +
      orderService.getFormattedWorkTimeRangeFromDate(
        option.startRestAt!,
        option.finishRestAt!
      )
    );
  }
  getVisibleToDate(option: OrderWorkTimeOption): number {
    if (!option) return 0;
    return parseISO(option.visibleTo);
  }

  isWorkTimeOptionAvailable(option: OrderWorkTimeOption) {
    if (!option) return false;
    return this.order.isHiring && parseISO(option.visibleTo) > now();
  }

  getSalaryPerDayWithVariousAllowances(option: OrderWorkTimeOption): string {
    if (!option) return "";
    return (
      formatNumber(
        orderService.getSalaryPerDayWithVariousAllowancesSpecifiedWorkTimeOption(
          this.order,
          option
        )
      ) || ""
    );
  }

  hasNightWorkSalary(option: OrderDetailWorkTimeOption) {
    return option.workSession.nightworkSalary > 0;
  }

  hasLegalOvertimeSalary(option: OrderDetailWorkTimeOption) {
    return option.workSession.legalOvertimeSalary > 0;
  }

  hasAppliedNightWorkSalary(application: Application) {
    return application.appliedWorkSession.nightworkSalary > 0;
  }

  hasAppliedLegalOvertimeSalary(application: Application) {
    return application.appliedWorkSession.legalOvertimeSalary > 0;
  }

  getOptimizedOrderImage(image: string | null) {
    if (isDataUri(image)) return image;
    if (this.isPreview && !image) {
      return require("@/assets/img/undefined.png");
    }
    return getOptimizedImageUrl(image, 768, null);
  }
  getOptimizedOrderImageSmall(image: string | null) {
    if (isDataUri(image)) return image;
    if (this.isPreview && !image) {
      return require("@/assets/img/undefined.png");
    }
    return getOptimizedImageUrl(image, 256, null);
  }
  onChangeSelectedWorkTimeOptionId(option: OrderWorkTimeOption) {
    if (!this.isWorkTimeOptionAvailable(option)) {
      return;
    }
    this.innerSelectedWorkTimeOptionId = option.id;
  }
}
</script>

<style lang="scss" scoped>
.order-card {
  padding: 12px;
  overflow: hidden;
  background-color: $white;
  border-top: 1px solid $gray-03;
  border-bottom: 1px solid $gray-03;
}

.order-card-header {
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
}

.order-card-carousel {
  .carousel-image {
    width: 100%;
    height: 300px;
    object-fit: cover;
  }
}

.order-card-images {
  position: relative;
  display: flex;
  margin-right: -16px;
  margin-left: -16px;

  &__item {
    width: calc(100% / 3);

    &:nth-child(2) {
      margin-right: 4px;
      margin-left: 4px;
    }
  }

  &__item-img {
    width: 100%;
    height: 100px;
    object-fit: cover;
  }
}

.order-card-title {
  margin-right: 16px;
  font-size: 18px;

  &--break {
    overflow-wrap: break-word;
  }

  &--ellipsis {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.office-info {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px;
  margin-top: 8px;
  border: 1px solid $gray-03;
  border-radius: 4px;

  .office-name-content {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    margin-bottom: 2px;
  }

  .office-name {
    font-size: 14px;
    font-weight: 500;
    line-height: 18px;
  }

  .office-address {
    font-size: 12px;
    font-weight: 400;
    line-height: 16px;
    color: $mild;
  }

  .office-actions {
    display: flex;
    gap: 6px;
    margin-left: auto;
  }
}

.work-time {
  .application,
  .work-time-options {
    > div.work-time-detail {
      width: 100%;
      padding: 8px;
      margin-bottom: 8px;
      border: 1px solid $gray-03;
      border-radius: 8px;

      &.expired {
        background-color: $gray-03 !important;
      }

      &:first-child {
        background: #fffded;

        .work-time {
          color: #31383f;
        }

        .rest-time {
          margin-left: 1em;
          font-size: 12px;
          color: #31383f;
        }

        .salary-per-day-with-various-allowances {
          color: #d23e36;
        }
      }
    }

    .work-time-option-radio :deep(label) {
      background-color: transparent;
    }

    .work-time {
      color: #66798a;
    }

    .rest-time {
      margin-left: 1em;
      font-size: 12px;
      color: #66798a;
    }

    .salary-per-day-with-various-allowances {
      font-size: 20px;
      font-weight: 700;
    }

    .salary-per-day-wrapper {
      display: flex;
      gap: 8px;
      align-items: center;
    }

    .extra-wage-tag {
      min-width: 56px;
      padding: 1px 3px;
      font-size: 12px;
      font-weight: 400;
      line-height: 18px;
      color: $mild;
      border-color: $mild;
    }
  }

  .reward-detail {
    display: flex;
    flex-direction: column;
    gap: 2px;

    .hourly-wage {
      font-size: 12px;
      font-weight: 600;
      line-height: 16px;
      color: $mild;
    }

    .various-allowances {
      font-size: 12px;
      font-weight: 600;
      line-height: 16px;
      color: $mild;
    }
  }
}

.caution-box {
  display: flex;
  align-items: center;
  padding: 8px;
  color: $red;
  background-color: #ffe3e1;
  border: 1px solid $red;
  border-radius: 4px;
}

.caution-text {
  color: $red;
}

.applicants-count-tag {
  color: white;
}

/* ABテスト2807のグループ別スタイル */
.ab-test-group-2807-b {
  background-color: $mild;
  border-color: $mild;
}

.ab-test-group-2807-c {
  background-color: #c2493e;
  border-color: #c2493e;
}
</style>
